// frontend/src/admin/v2/AdminSettings.jsx
import { Fragment, useState } from 'react'
import {
  Stack, Box, Typography, Chip, Skeleton, Alert, Switch, CircularProgress,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useSettings } from './hooks/useSettings'
import { SP, FS, hoverOnly } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import DisabledReason from '../../components/common/DisabledReason'
import Pill from '../../components/common/Pill'
import { formatBerlinDate } from '../../utils/time'
import AgentZahlen from './AgentZahlen'

/**
 * AdminSettings — /admin/settings page.
 *
 * @description
 *   Inventory of operational toggles. Entries with `source: "render_env"` are
 *   read-only here — staff changes them in the Render dashboard (a deploy).
 *   Entries with `source: "db"` carry a switch that writes straight to the
 *   database, so the change takes effect without a deploy (N102 Stufe A).
 */
const CATEGORY_META = {
  feature_flag: { label: 'Feature flags', description: 'Behaviour gates — turn whole capabilities on/off.' },
  kill_switch:  { label: 'Kill switches', description: 'Emergency disables. Should normally read "off".' },
  tuning:       { label: 'Tuning knobs', description: 'Numeric thresholds + pool sizes. Change cautiously.' },
  routing:      { label: 'Routing', description: 'Destination email addresses + URLs.' },
  // Stufe S der globalen Suche (Plan §S). Die dreizehn Arten-Schalter — einer je
  // durchsuchbarer Art — liegen als eigene Gruppe hier, nicht unter
  // „feature_flag": sie sind eine Auswahl, keine Funktionsfreigabe, und die
  // Beschreibung traegt den Satz, der beim Umlegen zaehlt (die Abwahl ist keine
  // Loeschung). Ohne diesen Eintrag rendert die Seite die Gruppe NICHT —
  // `Object.keys(CATEGORY_META)` ist die Liste der gezeichneten Gruppen.
  // 🔴 R16-W1/W2 (Nachbesserung 1): der Satz, der fuer die GRUPPE gilt, steht
  // hier und nicht mehr neunmal in den Zeilenbeschreibungen. „Not a privacy
  // measure" gehoert dazu: die Abwahl ist keine Loeschung und kein Schutz —
  // die Zeilen liegen weiter im Index (`docs/features/search.md`).
  search:       { label: 'Search — searchable types', description: 'Which types the global search returns. Hiding a type does not delete it — the rows stay in the index, and it is not a privacy measure.' },
}


/**
 * ValuePill — der Wert einer Einstellung, die man HIER nicht umlegen kann.
 *
 * @description
 *   🔴 F12 (UI-Durchgang 2 Teil 2). Bis zum 2026-10-07 zeichnete diese Zeile
 *   eine umrandete Pille mit farbigem Rand (`bgcolor: 'transparent'`, `border`
 *   in der Tonfarbe) — neben den echten `Switch`en derselben Liste sah sie wie
 *   ein zweiter Knopf aus, und der einzige Unterschied zwischen „an" und „aus"
 *   war die Randfarbe. Fotos (admin/settings Desktop): „Feature-Flags als
 *   eigene ‚on/off'-Outlined-Pillen, die letzte Zeile als echter MUI-Switch —
 *   zwei Schalterformen in einer Liste".
 *
 *   Jetzt der Hausbaustein `Pill` OHNE `onClick`: er rendert ein `<span>`,
 *   trägt also keine Knopfrolle, und die Aussage steht als Wort da („On"/"Off")
 *   statt nur als Farbe (Regel 22 Punkt 2 — Zustand nie allein über Farbe).
 *
 *   🔴 Die WORTE kommen aus `on`/`off`, nicht aus `setting.display_value`: der
 *   Server liefert dort genau diese zwei Zeichenketten (`settings.py`,
 *   `_serialize`), und die Anzeige schreibt sie gross. Es ist dieselbe Aussage
 *   in einer Schreibweise — keine zweite Wahrheit. Der nicht-boolesche Zweig
 *   darunter zeigt weiterhin `display_value`, weil er beliebige Werte traegt.
 */
function ValuePill({ setting }) {
  if (setting.boolean) {
    const on = setting.value === true || setting.value === 'on'
    const tone = on
      ? (setting.category === 'kill_switch' ? 'warn' : 'ok')
      : 'plain'
    return (
      <Pill tone={tone}>{on ? 'On' : 'Off'}</Pill>
    )
  }
  return (
    <Box
      component="span"
      sx={{
        fontSize: FS.xs, fontFamily: 'monospace', fontWeight: 500,
        px: 1, py: 0.25, borderRadius: 0.5,
        bgcolor: 'action.hover', color: 'text.primary',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: 240,
      }}
    >
      {setting.display_value}
    </Box>
  )
}


function LetzteAenderung({ setting }) {
  // Drei Lagen, dieselben drei, die der Server liefert (`_serialize_db`):
  // nicht lesbar / Zeile fehlt (Vorgabe gilt) / Wert steht da.
  if (setting.lesbar === false) {
    return (
      <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'error.main' }}>
        not readable — showing the default
      </Typography>
    )
  }
  if (!setting.is_set) {
    return (
      <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
        default, never changed
      </Typography>
    )
  }
  const wann = setting.updated_at ? formatBerlinDate(new Date(setting.updated_at)) : null
  return (
    <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
      {wann ? `changed ${wann}` : 'changed'}{setting.updated_by ? ` · ${setting.updated_by}` : ''}
    </Typography>
  )
}


/** Woher ein Wert kommt, den diese Seite nicht setzen kann (F12).
 *
 *  🔴 SICHTBAR, nicht nur als `title`/Tooltip: eine Zeile ohne Knopf sagt sonst
 *  nicht, wo die Einstellung hingehoert, und ein Tooltip an einem `<span>` ist
 *  auf dem Telefon unerreichbar. Der Satz steht in der Zeile, die er erklaert —
 *  die Seite trägt dieselbe Aussage zusätzlich einmal ganz oben im `Alert`;
 *  hier beantwortet sie die Frage an der Zeile selbst („warum kein Schalter?").
 *
 *  🔴 `text.secondary`, NICHT `text.disabled` (Regel-16-Befund C1 der Runde 1).
 *  `text.disabled` ist in POG nicht ueberschrieben und damit MUI's
 *  `rgba(0,0,0,0.38)` — auf Weiss komponiert das zu ~#9E9E9E, **~2,68 : 1**,
 *  unter AA. Verschaerfend: die Zeile liegt auf einem `:hover`-Hintergrund
 *  (`action.hover`), der Kontrast faellt beim Ueberfahren weiter. Dieselbe Zahl
 *  nennt die Schwesterstelle in `ProductDetail.jsx` (dort 2,66 : 1 gemessen),
 *  die aus demselben Grund auf `text.primary` gezogen wurde. `text.secondary`
 *  liegt in beiden Modi ueber 4,5 : 1 (hell ~5,7 : 1) und bleibt trotzdem eine
 *  Nebenzeile, kein Fliesstext.
 *
 *  🔴 Was diese Funktion NICHT entscheidet: WANN sie erscheint. Die Bedingung
 *  dafuer steht in `SettingRow` (`ausRenderEnv`) — hier wird nur der Satz
 *  gerendert. Wer die Bedingung sucht, sucht dort.
 */
function Quelle() {
  return (
    <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
      Read-only — set in the Render dashboard, applied on the next deploy.
    </Typography>
  )
}


function SettingRow({ setting, onSave, saving, fehler }) {
  const umlegbar = setting.source === 'db'
  // 🔴 Die ERKLAERZEILE haengt am NAMEN der Quelle, nicht an der Negation von
  // `umlegbar` (Regel-16-Befund W3). „Set in the Render dashboard" ist eine
  // Aussage ueber EINE Quelle; eine spaetere dritte bekaeme sie sonst
  // stillschweigend mitgeliefert. Heute gibt es genau zwei Quellen
  // (`backend/admin_v2/settings.py`: `render_env` und `db`) — die Bedingung ist
  // trotzdem an den Namen gebunden. Die dritte Quelle erscheint dann OHNE
  // Erklaerzeile, nicht mit einer falschen.
  // 🔴 Die Begruendung stand bis zur Folgerunde 5 im JSDoc von `Quelle()`
  // (Regel-16-Befund W-H) — dort beschrieb sie eine Entscheidung, die `Quelle()`
  // gar nicht trifft. Sie gehoert an die Bedingungszeile.
  const ausRenderEnv = setting.source === 'render_env'
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={SP.card}
      sx={{
        py: SP.card, px: SP.tight, borderRadius: 1,
        ...hoverOnly({ bgcolor: 'action.hover' }),
      }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Stack direction="row" alignItems="baseline" spacing={SP.tight} flexWrap="wrap">
          <Typography sx={{ fontSize: FS.sm, fontWeight: 500 }}>
            {setting.label}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled', fontFamily: 'monospace' }}>
            {setting.key}
          </Typography>
          {!setting.is_set && (
            <Chip
              label="default"
              size="small"
              sx={{
                height: 16, fontSize: '0.65rem',
                bgcolor: 'transparent', border: 1, borderColor: 'text.disabled',
                color: 'text.disabled',
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
          {setting.description}
        </Typography>
        {umlegbar ? <LetzteAenderung setting={setting} /> : (ausRenderEnv ? <Quelle /> : null)}
        {fehler && (
          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'error.main' }}>
            {fehler}
          </Typography>
        )}
      </Stack>
      {umlegbar ? (
        <Stack direction="row" alignItems="center" spacing={SP.tight} sx={{ flexShrink: 0 }}>
          {saving && <CircularProgress size={14} />}
          {/* 🔴 `inputProps` traegt den Namen in MUI 7 NICHT bis zum
              Eingabeelement (gemessen: `getByRole('switch', { name })` fand
              nichts, `getByRole('switch')` zaehlte fuenf). `slotProps.input`
              ist der Weg dieser Fassung — und `role` muss mit: MUI setzt
              `role: 'switch'` selbst in `slotProps.input` (Switch.js:285),
              und ein eigenes `slotProps.input` ERSETZT es, statt es zu
              ergaenzen. Ohne die Zeile faellt die Rolle weg und jede
              `getByRole('switch')`-Probe der Seite wird rot. */}
          <DisabledReason
            // 🔴 R16-W3 (Nachbesserung 1): der Grund hiess „This setting is
            // read-only." — das ist die Aussage ueber eine ANDERE Quelle
            // (`Quelle()`: render_env, „set in the Render dashboard"). Hier ist
            // die Zeile schreibbar und die ABFRAGE gescheitert; der Wert faellt
            // auf die Vorgabe zurueck (`_serialize_db`). Der alte Satz schickte
            // den Leser an die falsche Stelle. Der neue nennt Ursache UND was
            // angezeigt wird — die Hausform der Mikrocopy (Ursache + naechster
            // Schritt), und `Quelle()` daneben bleibt unveraendert.
            reason={saving
              ? null
              : setting.lesbar === false
                ? 'Could not read the saved value — showing the default.'
                : null}
          >
            <Switch
              size="small"
              checked={setting.value === true}
              disabled={saving || setting.lesbar === false}
              onChange={(e) => onSave(setting.key, e.target.checked)}
              slotProps={{ input: { role: 'switch', 'aria-label': setting.label } }}
            />
          </DisabledReason>
        </Stack>
      ) : (
        <ValuePill setting={setting} />
      )}
    </Stack>
  )
}


export default function AdminSettings() {
  const { data, loading, error, saveSetting } = useSettings()
  const [speichernd, setSpeichernd] = useState(null)
  // N102 B1: die Zahl der erfolgreichen Schreibvorgaenge. `AgentZahlen`
  // zeigt dieselben vier Reparaturschalter ein zweites Mal und holt seine
  // Daten nur beim Aufbau — ohne diesen Zaehler stuende daneben nach dem
  // Umlegen weiter der alte Stand.
  const [version, setVersion] = useState(0)
  const [speicherFehler, setSpeicherFehler] = useState({})
  const grouped = data?.grouped || {}

  const umlegen = async (key, wert) => {
    setSpeichernd(key)
    setSpeicherFehler(f => ({ ...f, [key]: null }))
    try {
      await saveSetting(key, wert)
      setVersion(v => v + 1)
    } catch (e) {
      const grund = e?.response?.data?.error || e?.message || 'failed'
      setSpeicherFehler(f => ({ ...f, [key]: grund }))
    } finally {
      setSpeichernd(null)
    }
  }

  return (
    <Stack spacing={SP.section}>
      {error && <Alert severity="error" variant="outlined">{error}</Alert>}

      <Alert
        icon={<LockOutlinedIcon fontSize="small" />}
        severity="info"
        variant="outlined"
        sx={{ fontSize: FS.xs }}
      >
        <strong>Some settings are live, the rest are read-only.</strong> Rows with
        a switch write straight to the database and take effect without a deploy.
        The rest come from Render env vars — change those in the dashboard, then
        redeploy.
      </Alert>

      {loading && !data ? (
        <Stack spacing={SP.card}>
          {[0, 1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={140} />)}
        </Stack>
      ) : (
        <Stack spacing={SP.section}>
          {Object.keys(CATEGORY_META).map(cat => {
            const items = grouped[cat] || []
            if (items.length === 0) return null
            const meta = CATEGORY_META[cat]
            return (
              <Stack
                key={cat}
                spacing={SP.card}
                // 🔴 Der Anker der GRUPPE (Regel-16-Befund B1, Nachbesserung 1).
                // `settings-schalter.spec.ts` zaehlte seitenweit `getByRole
                // ('switch')` — mit den dreizehn Sucharten sind es 18 statt 5, und
                // die Probe war rot. Eine Zahl ueber die GANZE Seite ist keine
                // Aussage ueber eine Gruppe; ueber diesen Anker zaehlt sie je
                // Gruppe, und `CATEGORY_META` bleibt die Quelle der Schluessel.
                data-testid={`setting-gruppe-${cat}`}
              >
                <Stack spacing={0.25}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: FS.xs, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                    }}
                  >
                    {meta.label} · {items.length}
                  </Typography>
                  {/* 🔴 `text.secondary`, nicht `text.disabled` (R16-W6). Dieselbe
                      Kombination ist in `SearchBereichWahl.jsx:686-689` mit rund
                      **2,6:1** gemessen — `text.disabled` auf `FS.xs` gegen die
                      geforderten 4,5:1. Der Satz, den diese Zeile traegt, ist
                      inhaltlich („die Abwahl ist keine Loeschung und keine
                      Datenschutzmassnahme") und kein ausgegrauter Zustand; er
                      stand hier nur zufaellig auf dem kontrastaermsten Token. */}
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                    {meta.description}
                  </Typography>
                </Stack>
                <Panel sx={{ p: SP.tight }}>
                  <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                    {items.map(s => (
                      <Fragment key={s.key}>
                        <SettingRow
                          setting={s}
                          onSave={umlegen}
                          saving={speichernd === s.key}
                          fehler={speicherFehler[s.key]}
                        />
                        {/* N102 A7: der Tagesstand direkt unter seinem Schalter.
                            Er steht INNERHALB des Stapels, damit die Trennlinie
                            des Nachbarn ihn mitfasst und er nicht als eigener
                            Block neben der Gruppe haengt. */}
                        {s.key === 'agent_anstoss_aktiv' && <AgentZahlen version={version} />}
                      </Fragment>
                    ))}
                  </Stack>
                </Panel>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}
